// Canonical gameplay constants restored from geometry_wars_3d_glm5_2.html.
// Balance values are product behavior: change them only with a new parity baseline.
export const CONFIG = {
  fixedStep: 1 / 60,
  maxSubsteps: 8,
  sphereRadius: 60,
  capHalfAngle: 0.448,
  worldBounds: 26,
  player: {
    radius: 0.55,
    speed: 16,
    boostSpeed: 26,
    accel: 90,
    friction: 6,
    maxHp: 100,
    fireInterval: 0.09,
    bulletSpeed: 42,
    bulletLife: 1.1,
    bulletDamage: 1,
    bulletRadius: 0.28,
    maxBoost: 1,
    boostDrain: 0.55,
    boostRegen: 0.18,
    invulnTime: 1.6,
    maxLives: 3,
    maxEnergy: 100,
    energyRegen: 8,
    energyPerKill: 2.2,
    skillCost: 100,
    skillDamage: 12,
    skillRadius: 14,
    maxWeaponLevel: 3,
    shieldTime: 1.4,
    weapons: {
      blaster: { interval: 0.09, damage: 1 },
      missile: { interval: 0.48, damage: 4.5, blastRadius: 2.6, turnRate: 5.4 },
      lightning: { interval: 0.38, damage: 1.8, range: 17 },
      laser: { interval: 0.22, damage: 1.65 },
    },
  },
  enemies: {
    grunt: { radius: 0.7, hp: 1, score: 100, speed: 7, spawnWeight: 5 },
    wanderer: { radius: 0.85, hp: 2, score: 250, speed: 9, spawnWeight: 3 },
    singularity: {
      radius: 1.6,
      hp: 6,
      score: 600,
      speed: 3.5,
      spawnWeight: 1,
      pullRadius: 9,
      explodeTime: 4,
      pullForce: 26,
    },
    dodger: {
      radius: 0.65,
      hp: 2,
      score: 400,
      speed: 11,
      spawnWeight: 2,
      dodgeRange: 6,
      dodgeSpeed: 24,
      dodgeCooldown: 1.4,
    },
  },
  score: { multiplierStep: 1.25, multiplierMax: 25, comboWindow: 3 },
  spawn: {
    baseInterval: 1.5,
    minInterval: 0.35,
    intervalDecay: 0.94,
    maxAlive: 60,
    maxAlivePerWave: 4,
    maxAliveCap: 140,
    waveBudgetGrowth: 4,
    spawnRingPad: 2,
  },
  particles: {
    perKill: 64,
    perBigKill: 150,
    maxParticles: 5000,
    lifespan: 1.5,
    damping: 0.9,
    speed: 17,
    muzzle: 8,
    pickup: 30,
    hitPlayer: 26,
  },
  camera: {
    fovBase: 62,
    fovBoost: 62,
    traumaDecay: 2.2,
    shakeAmp: 0.55,
    rollAmp: 0,
    shakeLambda: 8,
    eventWobbleDecay: 1.25,
    eventWobbleShift: 0.42,
    eventWobbleRoll: 0.012,
    eventWobbleFov: 1.6,
    eventEntityShift: 0.32,
    eventEntityLift: 0.16,
    eventEntityRoll: 0.035,
    eventEntityScale: 0.025,
    zoomMin: 0.78,
    zoomMax: 1.38,
    zoomWheelSpeed: 0.00075,
    zoomLambda: 7,
  },
  grid: { impulseStrength: 6, maxImpulses: 20 },
  // Secondary-weapon ultimate: one button, pure time cooldown (unlike NOVA's
  // energy cost). Fires the super version of the equipped secondary. New
  // section only — golden scenario never presses the button, so no rebaseline.
  ultimate: {
    cooldown: 45,
    missile: { count: 100, perWave: 6, waveInterval: 0.06, damageMul: 1.1, turnRate: 7.5, blastRadius: 2.9, speed: 17, life: 3.4 },
    // Tempest: 3 long straight lightning streams (3x a normal arc's length)
    // searing whatever they touch for ~5s, tracking the aim. Long bolts keep
    // addWeaponArc's zigzag readable — short orbit chords never did.
    lightning: {
      streams: 3, spreadDeg: 10, length: 24, duration: 5.0, interval: 0.05, damage: 0.5, width: 1.0,
    },
    laser: { duration: 5.0, burstInterval: 0.08, beamsPerBurst: 3, spreadDeg: 5, damageMul: 0.9, speed: 80, life: 0.5 },
  },
  lightningVisual: {
    displacement: 0.22,
    depth: 5,
    branchProbability: 0.55,
    lifetime: 0.1,
    flickerPeriod: 0.05,
    glowMultiplier: 3.5,
  },
  sfxSamples: {
    fire: { volume: 0.30, gap: 0.05, voices: 5 },
    explosion: { volume: 0.5, gap: 0.08, voices: 4 },
    bigkill: { volume: 0.6, gap: 0.15, voices: 2 },
    hit: { volume: 0.6, gap: 0.2, voices: 2 },
    pickup: { volume: 0.5, gap: 0.1, voices: 3 },
    multiplier: { volume: 0.45, gap: 0.1, voices: 2 },
    boss: { volume: 0.7, gap: 0, voices: 1 },
  },
  items: {
    visual: { scale: 1 },
    radius: 0.5,
    lifespan: 12,
    dropChance: 0.3,
    // Late-game economy: sustain drops (heal/life/shield) and overall drop
    // rate decay past startWave so runs can actually end. Weapons/multiplier
    // keep full weight — firepower stays, survival dries up.
    decay: { startWave: 5, perWave: 0.12, chanceFloor: 0.15, sustainFloor: 0.2 },
    bossDropCount: 6,
    // Bosses drop no weapons — grunts drop them often enough (weight 8).
    bossWeaponDrops: { mini: 0, big: 0 },
    dropWeights: { life: 5, heal: 4, weapon: 8, boost: 2, shield: 2, multiplier: 1 },
    magnetRadius: 11,
    magnetPull: 36,
    colors: {
      heal: 0x33ff88,
      boost: 0xff2bd6,
      weapon: 0xffdd33,
      life: 0x19f0ff,
      shield: 0x9966ff,
      multiplier: 0xff7733,
    },
  },
  boss: {
    miniWave: 3,
    bigWave: 10,
    firstDelay: 42,
    interval: 52,
    intervalMin: 38,
    postThirdIntervalCut: 5,
    postThirdIntervalMin: 26,
    postThirdHpGrowth: 0.45,
    postThirdSpeedGrowth: 0.08,
    postThirdFireAccel: 0.13,
    postThirdBulletSpeedGrowth: 0.09,
    postThirdDamageGrowth: 2,
    postThirdExtraBullets: 2,
    mini: {
      radius: 2.2,
      hp: 70,
      score: 5000,
      speed: 5,
      fireInterval: 1.1,
      bulletSpeed: 20,
      bulletLife: 3.5,
      bulletDamage: 14,
      color: 0x247cff,
    },
    big: {
      radius: 3.4,
      hp: 260,
      score: 25000,
      speed: 4,
      fireInterval: 0.7,
      bulletSpeed: 25,
      bulletLife: 4.5,
      bulletDamage: 18,
      color: 0xff4b1f,
    },
  },
} as const;

export type Config = typeof CONFIG;
